import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Building2, Gift } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const PLANS = [
    {
        id: "free",
        name: "Découverte",
        price: 0,
        badge: { label: "Gratuit", icon: Gift, className: "bg-[var(--tk-input-bg)] text-[var(--tk-text)] border border-[var(--tk-border)]" },
        tagline: "Pour découvrir la puissance de Ticketify",
        cta: "Commencer gratuitement",
        features: [
            "Relevés bancaires illimités",
            "Jusqu'à 100 transactions justifiées",
            "Connexion Gmail",
            "Recherche automatique des justificatifs",
            "Export PDF",
            "Historique 30 jours",
        ],
    },
    {
        id: "pro",
        name: "Pro",
        price: 39.99,
        badge: { label: "Le plus populaire", icon: Sparkles, className: "bg-[#007AFF] text-white shadow-[0_4px_14px_rgba(0,122,255,0.35)]" },
        tagline: "Pour les indépendants et TPE qui veulent gagner du temps",
        cta: "Passer au Pro",
        featured: true,
        features: [
            "Relevés bancaires illimités",
            "Jusqu'à 1 000 transactions justifiées / mois",
            "Connexion Gmail",
            "Association automatique des justificatifs",
            "Export CSV",
            "Export PDF",
            "Historique illimité",
            "Support prioritaire",
        ],
    },
    {
        id: "business",
        name: "Business",
        price: 149.99,
        badge: { label: "Entreprise", icon: Building2, className: "bg-[#34C759] text-white shadow-[0_4px_14px_rgba(52,199,89,0.3)]" },
        tagline: "Pour les équipes et cabinets à fort volume",
        cta: "Contacter l'équipe",
        features: [
            "Transactions illimitées",
            "Relevés illimités",
            "Multi-utilisateurs",
            "API",
            "Export CSV",
            "Export PDF",
            "Historique illimité",
            "Support dédié",
        ],
    },
];

export default function ForfaitPage() {
    const { user } = useAuth();
    const [, setPlans] = useState([]);
    const [busy, setBusy] = useState(null);
    const [cycle, setCycle] = useState("monthly");

    useEffect(() => {
        api.get("/payments/plans")
            .then((r) => setPlans(r.data))
            .catch(() => {});
    }, []);

    const priceFor = (p) => {
        if (p.price === 0) return { monthly: 0, billed: 0, save: 0, note: null };
        if (cycle === "quarterly") {
            const monthly = p.price * 0.9;
            return {
                monthly,
                billed: monthly * 3,
                save: (p.price - monthly) * 3,
                note: `Facturé ${(monthly * 3).toFixed(2).replace(".", ",")}€ tous les 3 mois`,
            };
        }
        if (cycle === "yearly") {
            const monthly = p.price * 0.8;
            return {
                monthly,
                billed: monthly * 12,
                save: (p.price - monthly) * 12,
                note: `Facturé ${(monthly * 12).toFixed(2).replace(".", ",")}€ par an`,
            };
        }
        return { monthly: p.price, billed: p.price, save: 0, note: "Facturé mensuellement" };
    };

    const handleUpgrade = async (planId) => {
        if (planId === user?.plan) {
            toast.info("Vous êtes déjà sur ce plan.");
            return;
        }
        if (planId === "free") {
            toast.info(
                "Pour rétrograder vers le plan gratuit, contactez le support depuis le Centre d'aide.",
            );
            return;
        }
        setBusy(planId);
        try {
            const { data } = await api.post("/payments/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
                billing_cycle: cycle,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur Stripe");
            setBusy(null);
        }
    };

    const labelFor = (p) => {
        if (user?.plan === p.id) return "Plan actuel";
        if (busy === p.id) return "Redirection…";
        return p.cta;
    };

    return (
        <div className="space-y-10">
            <header className="max-w-2xl">
                <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Gérer votre forfait</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#86868B]">
                    Choisissez le forfait adapté à votre activité. Tous les plans utilisent notre IA pour
                    retrouver automatiquement les justificatifs associés à vos transactions bancaires.
                </p>
                <p className="mt-1.5 text-xs text-[#86868B]">
                    Vous êtes actuellement sur le plan{" "}
                    <span className="font-semibold capitalize text-[#1D1D1F]">{user?.plan || "free"}</span>.
                </p>
                <div className="mt-5 inline-flex items-center gap-1 p-1 rounded-full bg-[#F5F5F7] border border-black/5">
                    {[
                        ["monthly", "Mensuel", null],
                        ["quarterly", "3 mois", "-10 %"],
                        ["yearly", "Annuel", "-20 %"],
                    ].map(([id, label, badge]) => (
                        <button
                            key={id}
                            onClick={() => setCycle(id)}
                            data-testid={`forfait-cycle-${id}`}
                            className={`px-4 py-1.5 rounded-full text-sm transition flex items-center gap-1.5 ${
                                cycle === id ? "bg-white text-[#1D1D1F] shadow-sm font-medium" : "text-[#86868B]"
                            }`}
                        >
                            {label}
                            {badge && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#34C759]/15 text-[#34C759]">
                                    {badge}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </header>

            <div className="grid md:grid-cols-3 gap-6 items-stretch md:items-center">
                {PLANS.map((p) => {
                    const current = user?.plan === p.id;
                    const featured = !!p.featured;
                    const BadgeIcon = p.badge.icon;
                    return (
                        <div
                            key={p.id}
                            data-testid={`forfait-card-${p.id}`}
                            className={`tk-card relative flex flex-col p-7 pt-9 rounded-[28px] ${
                                featured
                                    ? "border-2 border-[#007AFF] shadow-[0_20px_50px_rgba(0,122,255,0.15)] md:py-10 md:-my-4 md:scale-[1.02] z-10"
                                    : "hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5"
                            } ${current ? "ring-2 ring-[#34C759] ring-offset-2" : ""}`}
                        >
                            <span
                                className={`absolute -top-3.5 left-7 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full ${p.badge.className}`}
                            >
                                <BadgeIcon className="w-3 h-3" />
                                {p.badge.label}
                            </span>
                            {current && (
                                <span className="absolute top-4 right-4 text-[10px] font-semibold text-white px-2.5 py-1 rounded-full bg-[#34C759]">
                                    Plan actuel
                                </span>
                            )}

                            <p className="text-sm font-semibold tracking-tight text-[#1D1D1F]">{p.name}</p>
                            <p className="mt-0.5 text-xs text-[#86868B]">{p.tagline}</p>

                            <p className="mt-6 flex items-baseline gap-1">
                                <span className={`font-semibold tracking-tight text-[#1D1D1F] ${featured ? "text-5xl" : "text-4xl"}`}>
                                    {p.price === 0
                                        ? "0€"
                                        : `${priceFor(p).monthly.toFixed(2).replace(".", ",")}€`}
                                </span>
                                <span className="text-sm text-[#86868B] font-normal">/ mois</span>
                            </p>
                            {p.price > 0 && (
                                <p className="mt-1.5 text-xs" data-testid={`forfait-billing-note-${p.id}`}>
                                    {priceFor(p).save > 0 ? (
                                        <span className="text-[#34C759] font-semibold">
                                            Économisez {priceFor(p).save.toFixed(0)}€ · {priceFor(p).note}
                                        </span>
                                    ) : (
                                        <span className="text-[#86868B]">{priceFor(p).note}</span>
                                    )}
                                </p>
                            )}

                            <div className="mt-6 h-px bg-[var(--tk-border)]" />

                            <ul className="mt-6 space-y-3 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="text-sm flex items-start gap-2.5 text-[#1D1D1F]">
                                        <span
                                            className={`mt-0.5 flex-shrink-0 w-4.5 h-4.5 rounded-full flex items-center justify-center ${
                                                featured ? "bg-[rgba(0,122,255,0.12)]" : "bg-[rgba(52,199,89,0.12)]"
                                            }`}
                                            style={{ width: 18, height: 18 }}
                                        >
                                            <Check className={`w-3 h-3 ${featured ? "text-[#007AFF]" : "text-[#34C759]"}`} strokeWidth={3} />
                                        </span>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handleUpgrade(p.id)}
                                disabled={current || busy === p.id}
                                data-testid={`forfait-cta-${p.id}`}
                                className={`tk-btn w-full justify-center mt-8 ${
                                    current
                                        ? "tk-btn-secondary"
                                        : featured
                                          ? "tk-btn-blue shadow-[0_8px_20px_rgba(0,122,255,0.35)] hover:shadow-[0_10px_26px_rgba(0,122,255,0.45)]"
                                          : "tk-btn-secondary hover:!opacity-100 hover:bg-[#1D1D1F] hover:text-white"
                                }`}
                            >
                                {labelFor(p)}
                            </button>
                        </div>
                    );
                })}
            </div>

            <p className="text-xs text-[#86868B] text-center" data-testid="forfait-footer-note">
                ✓ Sans engagement&nbsp;&nbsp;•&nbsp;&nbsp;Paiement sécurisé par Stripe&nbsp;&nbsp;•&nbsp;&nbsp;Changement de forfait à tout moment
            </p>
        </div>
    );
}
